/**
 * Supabase auth helpers that avoid `supabase.auth.getSession()`.
 *
 * We observed `getSession()` hanging indefinitely in some production builds/environments.
 * These helpers read the persisted auth payload from localStorage (supabase-js default)
 * and provide safe token decoding + expiry checks.
 */

type JwtPayload = {
  exp?: number;
  sub?: string;
  email?: string;
  [key: string]: unknown;
};

const ACCESS_TOKEN_STORAGE_KEY = 'PW_ACCESS_TOKEN';

function safeJsonParse<T>(raw: string): T | null {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function safeAtob(input: string): string | null {
  try {
    if (typeof atob === 'function') return atob(input);
    const buf = (globalThis as any)?.Buffer;
    if (buf?.from) return buf.from(input, 'base64').toString('binary');
    return null;
  } catch {
    return null;
  }
}

function decodeBase64UrlJson<T>(b64Url: string): T | null {
  // JWT uses base64url (RFC 7515): '-' -> '+', '_' -> '/', padding optional
  const b64 = b64Url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + (b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4)));
  const decoded = safeAtob(padded);
  if (!decoded) return null;
  return safeJsonParse<T>(decoded);
}

function getProjectRefFromSupabaseUrl(url: string | undefined): string | null {
  try {
    if (!url) return null;
    const host = new URL(url).host; // e.g. abcd1234.supabase.co
    const firstLabel = host.split('.')[0];
    return firstLabel || null;
  } catch {
    return null;
  }
}

export function safeLocalStorageGetItem(key: string): string | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeLocalStorageSetItem(key: string, value: string): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage?.setItem(key, value);
  } catch {
    // ignore
  }
}

function safeLocalStorageRemoveItem(key: string): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage?.removeItem(key);
  } catch {
    // ignore
  }
}

export function getStoredAccessToken(): string | null {
  return safeLocalStorageGetItem(ACCESS_TOKEN_STORAGE_KEY);
}

export function setStoredAccessToken(accessToken: string | null): void {
  if (!accessToken) {
    safeLocalStorageRemoveItem(ACCESS_TOKEN_STORAGE_KEY);
    return;
  }
  safeLocalStorageSetItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);
}

function safeLocalStorageKeys(): string[] {
  try {
    if (typeof window === 'undefined') return [];
    const ls = window.localStorage;
    if (!ls) return [];
    const out: string[] = [];
    for (let i = 0; i < ls.length; i++) {
      const k = ls.key(i);
      if (k) out.push(k);
    }
    return out;
  } catch {
    return [];
  }
}

export function findSupabaseAuthStorageKey(supabaseUrl?: string): string | null {
  try {
    if (typeof window === 'undefined') return null;
    const ls = window.localStorage;
    if (!ls) return null;

    // Prefer the *current* project's key if we can infer it.
    const ref = getProjectRefFromSupabaseUrl(supabaseUrl);
    const preferred = ref ? `sb-${ref}-auth-token` : null;
    if (preferred && ls.getItem(preferred)) return preferred;

    // Fallback: scan for any supabase auth token key.
    // (Useful in tests, and also when SUPABASE_URL cannot be inferred.)
    for (const k of safeLocalStorageKeys()) {
      if (k.startsWith('sb-') && k.endsWith('-auth-token')) return k;
    }
    return null;
  } catch {
    return null;
  }
}

export function readSupabaseTokensFromLocalStorage(supabaseUrl?: string): {
  accessToken: string | null;
  refreshToken: string | null;
} {
  const preferredAccessToken = getStoredAccessToken();
  const key = findSupabaseAuthStorageKey(supabaseUrl);
  if (!key) return { accessToken: preferredAccessToken, refreshToken: null };

  const raw = safeLocalStorageGetItem(key);
  if (!raw) return { accessToken: preferredAccessToken, refreshToken: null };

  // Supabase persisted shape has varied. Handle common cases.
  const parsed = safeJsonParse<any>(raw);
  if (!parsed) return { accessToken: preferredAccessToken, refreshToken: null };

  const accessToken =
    preferredAccessToken ||
    parsed?.access_token ||
    parsed?.currentSession?.access_token ||
    parsed?.session?.access_token ||
    null;
  const refreshToken =
    parsed?.refresh_token ||
    parsed?.currentSession?.refresh_token ||
    parsed?.session?.refresh_token ||
    null;

  return { accessToken, refreshToken };
}

/**
 * Remove refresh tokens from Supabase local storage payloads while preserving
 * access token and user metadata needed for optimistic UI hydration.
 */
export function stripSupabaseRefreshTokenFromStorage(supabaseUrl?: string): void {
  const preferredKey = findSupabaseAuthStorageKey(supabaseUrl);
  const keys = preferredKey ? [preferredKey] : safeLocalStorageKeys();

  for (const key of keys) {
    if (!key.startsWith('sb-') || !key.endsWith('-auth-token')) continue;

    const raw = safeLocalStorageGetItem(key);
    if (!raw) continue;
    const parsed = safeJsonParse<any>(raw);
    if (!parsed || typeof parsed !== 'object') continue;

    let changed = false;

    if (Object.prototype.hasOwnProperty.call(parsed, 'refresh_token')) {
      delete parsed.refresh_token;
      changed = true;
    }
    if (parsed.currentSession && typeof parsed.currentSession === 'object') {
      if (Object.prototype.hasOwnProperty.call(parsed.currentSession, 'refresh_token')) {
        delete parsed.currentSession.refresh_token;
        changed = true;
      }
    }
    if (parsed.session && typeof parsed.session === 'object') {
      if (Object.prototype.hasOwnProperty.call(parsed.session, 'refresh_token')) {
        delete parsed.session.refresh_token;
        changed = true;
      }
    }

    if (changed) {
      safeLocalStorageSetItem(key, JSON.stringify(parsed));
    }
  }
}

/**
 * Synchronize refreshed access token into Supabase storage payloads so
 * supabase-js clients that still read from storage keep working without a
 * persisted refresh token.
 */
export function syncSupabaseAccessTokenInStorage(accessToken: string, supabaseUrl?: string): void {
  if (!accessToken) return;
  const preferredKey = findSupabaseAuthStorageKey(supabaseUrl);
  const keys = preferredKey ? [preferredKey] : safeLocalStorageKeys();

  for (const key of keys) {
    if (!key.startsWith('sb-') || !key.endsWith('-auth-token')) continue;

    const raw = safeLocalStorageGetItem(key);
    if (!raw) continue;
    const parsed = safeJsonParse<any>(raw);
    if (!parsed || typeof parsed !== 'object') continue;

    parsed.access_token = accessToken;
    if (parsed.currentSession && typeof parsed.currentSession === 'object') {
      parsed.currentSession.access_token = accessToken;
    }
    if (parsed.session && typeof parsed.session === 'object') {
      parsed.session.access_token = accessToken;
    }

    safeLocalStorageSetItem(key, JSON.stringify(parsed));
  }
}

export function decodeJwtPayload(token: string): JwtPayload | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  return decodeBase64UrlJson<JwtPayload>(parts[1]);
}

export function isTokenExpiredOrExpiring(token: string, bufferSeconds = 60): boolean {
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;
  if (!exp || typeof exp !== 'number') return true;
  const now = Math.floor(Date.now() / 1000);
  return exp - now < bufferSeconds;
}

export function getUserIdFromAccessToken(token: string): string | null {
  const payload = decodeJwtPayload(token);
  const sub = payload?.sub;
  return typeof sub === 'string' && sub.length > 0 ? sub : null;
}
